import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './Analytics.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

// Chart colors
const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];
const TYPE_COLORS = {
  timer: '#667eea',
  image: '#43e97b',
  video: '#f5576c',
  carousel: '#4facfe'
};
const TYPE_LABELS = {
  timer: '計時器',
  image: '圖片',
  video: '影片',
  carousel: '輪播'
};

// CSV 導出工具函數
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert('沒有數據可導出');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Convert to CSV
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        let cell = row[header];
        // Handle objects and arrays
        if (typeof cell === 'object') {
          cell = JSON.stringify(cell);
        }
        // Escape quotes and wrap in quotes if contains comma
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
          cell = `"${cell.replace(/"/g, '""')}"`;
        }
        return cell ?? '';
      }).join(',')
    )
  ].join('\n');

  // Add BOM for Excel to recognize UTF-8
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [displays, setDisplays] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [detailed, setDetailed] = useState(null);
  const [timelineByType, setTimelineByType] = useState([]);
  const [contentDetails, setContentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date range filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedContent, setExpandedContent] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Fetch all data in parallel
      const [overviewRes, displaysRes, contentTypesRes, timelineRes, detailedRes, timelineByTypeRes, contentDetailsRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/overview`, { params }),
        axios.get(`${API_URL}/analytics/displays`, { params }),
        axios.get(`${API_URL}/analytics/content-types`, { params }),
        axios.get(`${API_URL}/analytics/timeline`, { params: { ...params, interval: 'day' } }),
        axios.get(`${API_URL}/analytics/detailed`, { params }),
        axios.get(`${API_URL}/analytics/timeline-by-type`, { params: { ...params, interval: 'day' } }),
        axios.get(`${API_URL}/analytics/content-details`, { params })
      ]);

      setOverview(overviewRes.data);
      setDisplays(displaysRes.data);
      setContentTypes(contentTypesRes.data);
      setTimeline(timelineRes.data);
      setDetailed(detailedRes.data);
      setTimelineByType(timelineByTypeRes.data);
      setContentDetails(contentDetailsRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  // Fetch when dates change
  useEffect(() => {
    if (startDate && endDate) {
      fetchAnalytics();
    }
  }, [startDate, endDate]);

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0秒';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}時${minutes}分`;
    if (minutes > 0) return `${minutes}分${secs}秒`;
    return `${secs}秒`;
  };

  // Format number
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('zh-TW');
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Export functions
  const exportOverview = () => {
    if (!overview) return;
    const data = [{
      活躍顯示器: overview.totalDisplays,
      總Sessions: overview.totalSessions,
      內容播放次數: overview.totalContentPlays,
      平均Session時長_秒: overview.avgSessionDuration,
      計時器播放: overview.contentTypeBreakdown?.timer || 0,
      圖片播放: overview.contentTypeBreakdown?.image || 0,
      影片播放: overview.contentTypeBreakdown?.video || 0,
      輪播播放: overview.contentTypeBreakdown?.carousel || 0,
      開始日期: startDate,
      結束日期: endDate
    }];
    exportToCSV(data, '統計總覽');
  };

  const exportDisplays = () => {
    if (!displays || displays.length === 0) return;
    const data = displays.map(d => ({
      顯示器名稱: d.displayName,
      顯示器ID: d.displayId,
      公司ID: d.companyId,
      Sessions: d.sessions,
      計時器: d.contentTypes?.timer || 0,
      圖片: d.contentTypes?.image || 0,
      影片: d.contentTypes?.video || 0,
      輪播: d.contentTypes?.carousel || 0,
      總播放次數: d.contentPlays,
      平均Session時長_秒: d.avgSessionDuration,
      最後活動: d.lastActivity ? new Date(d.lastActivity).toLocaleString('zh-TW') : ''
    }));
    exportToCSV(data, '顯示器統計');
  };

  const exportDetailed = (type) => {
    if (!detailed || !detailed[type]) return;
    const data = detailed[type].byDisplay.map(d => ({
      顯示器名稱: d.displayName,
      顯示器ID: d.displayId,
      播放次數: d.count
    }));
    exportToCSV(data, `${TYPE_LABELS[type] || type}統計`);
  };

  const exportTimeline = () => {
    if (!timelineByType || timelineByType.length === 0) return;
    const data = timelineByType.map(t => ({
      日期: t.date,
      計時器: t.timer,
      圖片: t.image,
      影片: t.video,
      輪播: t.carousel,
      總計: t.total
    }));
    exportToCSV(data, '時間趨勢');
  };

  const exportContentDetails = (type) => {
    if (!contentDetails || !contentDetails[type]) return;
    const data = contentDetails[type].map(c => ({
      內容名稱: c.contentName,
      內容URL: c.contentUrl,
      總播放次數: c.totalPlays,
      總播放時長_秒: c.totalDuration || 0,
      平均播放時長_秒: c.avgDuration || 0,
      首次播放: c.firstPlay ? new Date(c.firstPlay).toLocaleString('zh-TW') : '',
      最後播放: c.lastPlay ? new Date(c.lastPlay).toLocaleString('zh-TW') : '',
      使用顯示器數: c.byDisplay?.length || 0
    }));
    exportToCSV(data, `${TYPE_LABELS[type] || type}詳細統計`);
  };

  const exportAll = () => {
    // Export all data as multiple sections in one CSV
    let csvContent = '';
    const BOM = '\uFEFF';

    // Overview section
    if (overview) {
      csvContent += '=== 統計總覽 ===\n';
      csvContent += '項目,數值\n';
      csvContent += `活躍顯示器,${overview.totalDisplays}\n`;
      csvContent += `總Sessions,${overview.totalSessions}\n`;
      csvContent += `內容播放次數,${overview.totalContentPlays}\n`;
      csvContent += `平均Session時長(秒),${overview.avgSessionDuration}\n`;
      csvContent += `計時器播放,${overview.contentTypeBreakdown?.timer || 0}\n`;
      csvContent += `圖片播放,${overview.contentTypeBreakdown?.image || 0}\n`;
      csvContent += `影片播放,${overview.contentTypeBreakdown?.video || 0}\n`;
      csvContent += `輪播播放,${overview.contentTypeBreakdown?.carousel || 0}\n`;
      csvContent += '\n';
    }

    // Displays section
    if (displays && displays.length > 0) {
      csvContent += '=== 顯示器統計 ===\n';
      csvContent += '顯示器名稱,Sessions,計時器,圖片,影片,輪播,總播放,平均時長(秒),最後活動\n';
      displays.forEach(d => {
        csvContent += `${d.displayName},${d.sessions},${d.contentTypes?.timer || 0},${d.contentTypes?.image || 0},${d.contentTypes?.video || 0},${d.contentTypes?.carousel || 0},${d.contentPlays},${d.avgSessionDuration},${d.lastActivity ? new Date(d.lastActivity).toLocaleString('zh-TW') : ''}\n`;
      });
      csvContent += '\n';
    }

    // Timeline section
    if (timelineByType && timelineByType.length > 0) {
      csvContent += '=== 時間趨勢 ===\n';
      csvContent += '日期,計時器,圖片,影片,輪播,總計\n';
      timelineByType.forEach(t => {
        csvContent += `${t.date},${t.timer},${t.image},${t.video},${t.carousel},${t.total}\n`;
      });
    }

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `完整統計報表_${startDate}_${endDate}.csv`;
    link.click();
  };

  // Pie chart label
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  if (loading && !overview) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">載入中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">錯誤：{error}</div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>📊 顯示器統計分析</h1>

        <div className="analytics-filters">
          <div className="filter-group">
            <label>開始日期：</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>結束日期：</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <button onClick={fetchAnalytics} className="refresh-btn">
            🔄 重新整理
          </button>

          <button onClick={exportAll} className="export-btn">
            📥 導出全部
          </button>

          <button onClick={() => window.history.back()} className="back-btn">
            ← 返回
          </button>
        </div>

        {/* Tabs */}
        <div className="analytics-tabs">
          <button
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            總覽
          </button>
          <button
            className={`tab-btn ${activeTab === 'detailed' ? 'active' : ''}`}
            onClick={() => setActiveTab('detailed')}
          >
            詳細分類
          </button>
          <button
            className={`tab-btn ${activeTab === 'displays' ? 'active' : ''}`}
            onClick={() => setActiveTab('displays')}
          >
            顯示器統計
          </button>
          <button
            className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            內容詳情
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Overview Cards */}
          {overview && (
            <div className="analytics-overview">
              <div className="stat-card">
                <div className="stat-value">{formatNumber(overview.totalDisplays)}</div>
                <div className="stat-label">活躍顯示器</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatNumber(overview.totalSessions)}</div>
                <div className="stat-label">總 Sessions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatNumber(overview.totalContentPlays)}</div>
                <div className="stat-label">內容播放次數</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatDuration(overview.avgSessionDuration)}</div>
                <div className="stat-label">平均 Session 時長</div>
              </div>
            </div>
          )}

          {/* Content Type Cards */}
          {detailed && (
            <div className="detailed-cards">
              {Object.entries(detailed).map(([type, data]) => (
                <div key={type} className="type-card" style={{ borderColor: TYPE_COLORS[type] }}>
                  <div className="type-header" style={{ background: TYPE_COLORS[type] }}>
                    <span className="type-icon">
                      {type === 'timer' ? '⏱️' : type === 'image' ? '🖼️' : type === 'video' ? '🎬' : '🎠'}
                    </span>
                    <span className="type-name">{TYPE_LABELS[type] || type}</span>
                  </div>
                  <div className="type-body">
                    <div className="type-stat">
                      <span className="stat-number">{formatNumber(data.total)}</span>
                      <span className="stat-text">播放次數</span>
                    </div>
                    <div className="type-stat">
                      <span className="stat-number">{formatNumber(data.uniqueCount)}</span>
                      <span className="stat-text">不重複項目</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Timeline by Type Chart */}
          {timelineByType.length > 0 && (
            <div className="analytics-section chart-section">
              <h2>📈 內容類型趨勢</h2>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={timelineByType}>
                  <defs>
                    <linearGradient id="colorTimer" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TYPE_COLORS.timer} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={TYPE_COLORS.timer} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorImage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TYPE_COLORS.image} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={TYPE_COLORS.image} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorVideo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TYPE_COLORS.video} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={TYPE_COLORS.video} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorCarousel" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TYPE_COLORS.carousel} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={TYPE_COLORS.carousel} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" stroke="#666" fontSize={12} />
                  <YAxis stroke="#666" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="timer" name="計時器" stroke={TYPE_COLORS.timer} fillOpacity={1} fill="url(#colorTimer)" stackId="1" />
                  <Area type="monotone" dataKey="image" name="圖片" stroke={TYPE_COLORS.image} fillOpacity={1} fill="url(#colorImage)" stackId="1" />
                  <Area type="monotone" dataKey="video" name="影片" stroke={TYPE_COLORS.video} fillOpacity={1} fill="url(#colorVideo)" stackId="1" />
                  <Area type="monotone" dataKey="carousel" name="輪播" stroke={TYPE_COLORS.carousel} fillOpacity={1} fill="url(#colorCarousel)" stackId="1" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Content Type Pie */}
          <div className="charts-row">
            {contentTypes.length > 0 && (
              <div className="analytics-section chart-section pie-section">
                <h2>📁 內容類型分布</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={contentTypes.map(ct => ({ ...ct, name: TYPE_LABELS[ct.type] || ct.type }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="name"
                    >
                      {contentTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TYPE_COLORS[entry.type] || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detailed Tab */}
      {activeTab === 'detailed' && detailed && (
        <div className="detailed-tab">
          {Object.entries(detailed).map(([type, data]) => (
            <div key={type} className="analytics-section detailed-section">
              <div className="section-header">
                <h2 style={{ color: TYPE_COLORS[type] }}>
                  {type === 'timer' ? '⏱️ 計時器統計' : type === 'image' ? '🖼️ 圖片統計' : type === 'video' ? '🎬 影片統計' : '🎠 輪播統計'}
                </h2>
                <button onClick={() => exportDetailed(type)} className="export-btn-sm">
                  📥 導出
                </button>
              </div>

              <div className="detailed-overview">
                <div className="detailed-stat" style={{ borderColor: TYPE_COLORS[type] }}>
                  <div className="stat-value">{formatNumber(data.total)}</div>
                  <div className="stat-label">總播放次數</div>
                </div>
                <div className="detailed-stat" style={{ borderColor: TYPE_COLORS[type] }}>
                  <div className="stat-value">{formatNumber(data.uniqueCount)}</div>
                  <div className="stat-label">不重複{TYPE_LABELS[type]}</div>
                </div>
                <div className="detailed-stat" style={{ borderColor: TYPE_COLORS[type] }}>
                  <div className="stat-value">{data.byDisplay?.length || 0}</div>
                  <div className="stat-label">使用的顯示器</div>
                </div>
              </div>

              {/* By Display Bar Chart */}
              {data.byDisplay && data.byDisplay.length > 0 && (
                <div className="detailed-chart">
                  <h3>各顯示器使用次數</h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, data.byDisplay.length * 35)}>
                    <BarChart
                      layout="vertical"
                      data={data.byDisplay.slice(0, 10)}
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" stroke="#666" fontSize={12} />
                      <YAxis dataKey="displayName" type="category" stroke="#666" fontSize={12} width={90} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="播放次數" fill={TYPE_COLORS[type]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Recent Activity */}
              {data.recentActivity && data.recentActivity.length > 0 && (
                <div className="recent-activity">
                  <h3>最近活動</h3>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>顯示器</th>
                        <th>時間</th>
                        <th>內容</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentActivity.map((activity, idx) => (
                        <tr key={idx}>
                          <td>{activity.displayName}</td>
                          <td>{formatTime(activity.timestamp)}</td>
                          <td>
                            <code>
                              {activity.contentData?.timerName ||
                               activity.contentData?.carouselName ||
                               activity.contentData?.itemUrl?.substring(0, 50) ||
                               '-'}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Displays Tab */}
      {activeTab === 'displays' && displays.length > 0 && (
        <div className="analytics-section">
          <div className="section-header">
            <h2>📺 各顯示器詳細統計</h2>
            <button onClick={exportDisplays} className="export-btn-sm">
              📥 導出 CSV
            </button>
          </div>
          <div className="table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>顯示器名稱</th>
                  <th>Sessions</th>
                  <th>計時器</th>
                  <th>圖片</th>
                  <th>影片</th>
                  <th>輪播</th>
                  <th>總播放</th>
                  <th>平均時長</th>
                  <th>最後活動</th>
                </tr>
              </thead>
              <tbody>
                {displays.map(display => (
                  <tr key={display.displayId}>
                    <td>{display.displayName}</td>
                    <td>{formatNumber(display.sessions)}</td>
                    <td>{formatNumber(display.contentTypes?.timer || 0)}</td>
                    <td>{formatNumber(display.contentTypes?.image || 0)}</td>
                    <td>{formatNumber(display.contentTypes?.video || 0)}</td>
                    <td>{formatNumber(display.contentTypes?.carousel || 0)}</td>
                    <td><strong>{formatNumber(display.contentPlays)}</strong></td>
                    <td>{formatDuration(display.avgSessionDuration)}</td>
                    <td>{display.lastActivity ? new Date(display.lastActivity).toLocaleString('zh-TW') : '無'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content Details Tab */}
      {activeTab === 'content' && contentDetails && (
        <div className="content-details-tab">
          {/* Videos Section */}
          {contentDetails.video && contentDetails.video.length > 0 && (
            <div className="analytics-section">
              <div className="section-header">
                <h2>🎬 影片播放詳情 ({contentDetails.video.length} 個)</h2>
                <button onClick={() => exportContentDetails('video')} className="export-btn-sm">
                  📥 導出
                </button>
              </div>
              <div className="content-list">
                {contentDetails.video.map((content, idx) => (
                  <div key={idx} className="content-item" style={{ borderLeftColor: TYPE_COLORS.video }}>
                    <div className="content-main" onClick={() => setExpandedContent(expandedContent === `video-${idx}` ? null : `video-${idx}`)}>
                      <div className="content-info">
                        <span className="content-name">{content.contentName}</span>
                        <span className="content-url">{content.contentUrl}</span>
                      </div>
                      <div className="content-stats">
                        <span className="play-count">{formatNumber(content.totalPlays)} 次播放</span>
                        {content.totalDuration > 0 && (
                          <span className="duration-count" style={{ color: '#f5576c' }}>
                            ⏱️ {formatDuration(content.totalDuration)}
                          </span>
                        )}
                        <span className="display-count">{content.byDisplay?.length || 0} 個顯示器</span>
                        <span className="expand-icon">{expandedContent === `video-${idx}` ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {expandedContent === `video-${idx}` && (
                      <div className="content-details">
                        <div className="detail-row">
                          <span>首次播放：{formatTime(content.firstPlay)}</span>
                          <span>最後播放：{formatTime(content.lastPlay)}</span>
                        </div>
                        {content.totalDuration > 0 && (
                          <div className="detail-row" style={{ color: '#f5576c', fontWeight: 'bold' }}>
                            <span>總播放時長：{formatDuration(content.totalDuration)}</span>
                            <span>平均每次：{formatDuration(content.avgDuration)}</span>
                          </div>
                        )}
                        <h4>各顯示器播放次數：</h4>
                        <ul className="display-list">
                          {content.byDisplay?.map((d, i) => (
                            <li key={i}>
                              {d.displayName}: {d.count} 次
                              {d.totalDuration > 0 && <span style={{ color: '#f5576c' }}> (時長: {formatDuration(d.totalDuration)})</span>}
                              <span style={{ color: '#999' }}> (最後: {formatTime(d.lastPlay)})</span>
                            </li>
                          ))}
                        </ul>
                        {content.playHistory && content.playHistory.length > 0 && (
                          <>
                            <h4>最近播放記錄：</h4>
                            <ul className="history-list">
                              {content.playHistory.slice(0, 5).map((h, i) => (
                                <li key={i}>
                                  {h.displayName} - {formatTime(h.timestamp)}
                                  {h.duration && <span style={{ color: '#f5576c' }}> ({formatDuration(h.duration)})</span>}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images Section */}
          {contentDetails.image && contentDetails.image.length > 0 && (
            <div className="analytics-section">
              <div className="section-header">
                <h2>🖼️ 圖片播放詳情 ({contentDetails.image.length} 個)</h2>
                <button onClick={() => exportContentDetails('image')} className="export-btn-sm">
                  📥 導出
                </button>
              </div>
              <div className="content-list">
                {contentDetails.image.slice(0, 50).map((content, idx) => (
                  <div key={idx} className="content-item" style={{ borderLeftColor: TYPE_COLORS.image }}>
                    <div className="content-main" onClick={() => setExpandedContent(expandedContent === `image-${idx}` ? null : `image-${idx}`)}>
                      <div className="content-info">
                        <span className="content-name">{content.contentName}</span>
                      </div>
                      <div className="content-stats">
                        <span className="play-count">{formatNumber(content.totalPlays)} 次播放</span>
                        <span className="display-count">{content.byDisplay?.length || 0} 個顯示器</span>
                        <span className="expand-icon">{expandedContent === `image-${idx}` ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {expandedContent === `image-${idx}` && (
                      <div className="content-details">
                        <div className="detail-row">
                          <span>首次播放：{formatTime(content.firstPlay)}</span>
                          <span>最後播放：{formatTime(content.lastPlay)}</span>
                        </div>
                        <h4>各顯示器播放次數：</h4>
                        <ul className="display-list">
                          {content.byDisplay?.map((d, i) => (
                            <li key={i}>{d.displayName}: {d.count} 次</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
                {contentDetails.image.length > 50 && (
                  <p className="more-hint">還有 {contentDetails.image.length - 50} 個圖片，請導出查看完整列表</p>
                )}
              </div>
            </div>
          )}

          {/* Timers Section */}
          {contentDetails.timer && contentDetails.timer.length > 0 && (
            <div className="analytics-section">
              <div className="section-header">
                <h2>⏱️ 計時器顯示詳情 ({contentDetails.timer.length} 個)</h2>
                <button onClick={() => exportContentDetails('timer')} className="export-btn-sm">
                  📥 導出
                </button>
              </div>
              <div className="content-list">
                {contentDetails.timer.map((content, idx) => (
                  <div key={idx} className="content-item" style={{ borderLeftColor: TYPE_COLORS.timer }}>
                    <div className="content-main" onClick={() => setExpandedContent(expandedContent === `timer-${idx}` ? null : `timer-${idx}`)}>
                      <div className="content-info">
                        <span className="content-name">{content.contentName}</span>
                      </div>
                      <div className="content-stats">
                        <span className="play-count">{formatNumber(content.totalPlays)} 次顯示</span>
                        <span className="display-count">{content.byDisplay?.length || 0} 個顯示器</span>
                        <span className="expand-icon">{expandedContent === `timer-${idx}` ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {expandedContent === `timer-${idx}` && (
                      <div className="content-details">
                        <div className="detail-row">
                          <span>首次顯示：{formatTime(content.firstPlay)}</span>
                          <span>最後顯示：{formatTime(content.lastPlay)}</span>
                        </div>
                        <h4>各顯示器使用次數：</h4>
                        <ul className="display-list">
                          {content.byDisplay?.map((d, i) => (
                            <li key={i}>{d.displayName}: {d.count} 次</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!contentDetails.video || contentDetails.video.length === 0) &&
           (!contentDetails.image || contentDetails.image.length === 0) &&
           (!contentDetails.timer || contentDetails.timer.length === 0) && (
            <div className="analytics-empty">
              <p>所選時間範圍內沒有內容播放數據</p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {overview && overview.totalRecords === 0 && (
        <div className="analytics-empty">
          <p>所選時間範圍內沒有數據</p>
          <p>請嘗試選擇不同的日期範圍</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;
